import React, { useState, useMemo } from 'react';
import { FiX, FiPlus, FiAlertCircle } from 'react-icons/fi';
import styles from '../Auth.module.css';

/**
 * InterestsSelector - Multi-select chips for interests with category grouping
 */
const InterestsSelector = ({
  interestsData,
  selectedInterests = [],
  onInterestToggle,
  maxInterests = 20,
  label = "Interests",
  placeholder = "Select interests...",
  error,
  disabled = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  // Filter interests based on search
  const filteredCategories = useMemo(() => {
    if (!interestsData?.categories) return {};
    
    const query = searchQuery.toLowerCase().trim();
    if (!query) return interestsData.categories;

    const filtered = {};
    Object.entries(interestsData.categories).forEach(([key, categoryData]) => {
      const matchingTopics = categoryData.topics.filter(topic =>
        topic.toLowerCase().includes(query)
      );
      if (matchingTopics.length > 0) {
        filtered[key] = {
          ...categoryData,
          topics: matchingTopics,
        };
      }
    });
    return filtered;
  }, [interestsData, searchQuery]);

  // Toggle category expansion
  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };

  // Handle interest toggle
  const handleToggle = (interest) => {
    if (selectedInterests.includes(interest)) {
      onInterestToggle(selectedInterests.filter(i => i !== interest));
    } else if (selectedInterests.length < maxInterests) {
      onInterestToggle([...selectedInterests, interest]);
    }
  };

  // Remove interest
  const removeInterest = (interest, e) => {
    e.stopPropagation();
    onInterestToggle(selectedInterests.filter(i => i !== interest));
  };

  const remainingCount = maxInterests - selectedInterests.length;

  return (
    <div className={styles['au-interests-selector']}>
      {/* Header with count */}
      <div className={styles['au-interests-header']}>
        <label className={styles['au-input-group__label']}>{label}</label>
        <span className={`${styles['au-interests-count']} ${
          remainingCount === 0 ? styles['au-interests-count--full'] : ''
        }`}>
          {selectedInterests.length}/{maxInterests}
        </span>
      </div>

      {/* Selected interests chips */}
      {selectedInterests.length > 0 && (
        <div className={styles['au-interests-selected']}>
          {selectedInterests.map((interest) => (
            <span key={interest} className={styles['au-interests-chip']}>
              {interest}
              <button
                type="button"
                className={styles['au-interests-chip__remove']}
                onClick={(e) => removeInterest(interest, e)}
                disabled={disabled}
              >
                <FiX size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={placeholder}
        className={`${styles['au-input']} ${styles['au-interests-search']}`}
        disabled={disabled || remainingCount === 0}
      />

      {/* Categories with interests */}
      <div className={styles['au-interests-categories']}>
        {Object.entries(filteredCategories).map(([categoryKey, categoryData]) => {
          const isExpanded = expandedCategories[categoryKey] !== false;
          const categoryTopics = categoryData.topics || [];
          
          return (
            <div key={categoryKey} className={styles['au-interests-category']}>
              <button
                type="button"
                className={styles['au-interests-category__header']}
                onClick={() => toggleCategory(categoryKey)}
              >
                <span>{categoryData.category}</span>
                <span className={styles['au-interests-category__count']}>
                  {categoryTopics.filter(t => selectedInterests.includes(t)).length}/{categoryTopics.length}
                </span>
              </button>
              
              {isExpanded && (
                <div className={styles['au-interests-category__topics']}>
                  {categoryTopics.map((topic) => {
                    const isSelected = selectedInterests.includes(topic);
                    const isDisabled = disabled || (!isSelected && remainingCount === 0);
                    
                    return (
                      <button
                        type="button"
                        key={topic}
                        className={`${styles['au-interests-topic']} ${
                          isSelected ? styles['au-interests-topic--selected'] : ''
                        }`}
                        onClick={() => handleToggle(topic)}
                        disabled={isDisabled}
                      >
                        {isSelected ? <FiX size={12} /> : <FiPlus size={12} />}
                        {topic}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Error display */}
      {error && (
        <div className={styles['au-input-group__error']}>
          <FiAlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default InterestsSelector;
